import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { User } from 'firebase/auth'
import type { Language, Translation } from '../../../translations'
import { SearchHeader } from '../components/SearchHeader'
import { SavedSearchChips } from '../components/SavedSearchChips'
import { SearchResultsList } from '../components/SearchResultsList'
import type { SearchResult } from '../types'

const SUGGEST_URL = 'https://europe-west6-dxt-data.cloudfunctions.net/suggest_law'
const SAVED_SEARCHES_KEY = 'jurimaroc.savedSearches'

interface SearchPageProps {
    lang: Language
    t: Translation
    user: User | null
    onRequireLogin: () => Promise<void>
    onShowUpgrade: () => void
}

export function SearchPage({ lang, t, user, onRequireLogin, onShowUpgrade }: SearchPageProps) {
    const [query, setQuery] = useState('')
    const [savedSearches, setSavedSearches] = useState<string[]>([])

    const [isSearchSuggestOpen, setIsSearchSuggestOpen] = useState(false)
    const [searchSuggestIndex, setSearchSuggestIndex] = useState(-1)
    const [remoteSearchSuggestions, setRemoteSearchSuggestions] = useState<string[]>([])
    const suggestAbortRef = useRef<AbortController | null>(null)
    const suggestTimerRef = useRef<number | null>(null)

    const [results, setResults] = useState<SearchResult[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [nextPageToken, setNextPageToken] = useState<string | null>(null)
    const [totalSize, setTotalSize] = useState<number | null>(null)
    const [correctedQuery, setCorrectedQuery] = useState<string | null>(null)
    const [expandedResultIds, setExpandedResultIds] = useState<Record<string, boolean>>({})

    useEffect(() => {
        try {
            const stored = localStorage.getItem(SAVED_SEARCHES_KEY)
            if (stored) {
                const parsed = JSON.parse(stored)
                if (Array.isArray(parsed)) {
                    setSavedSearches(parsed.filter((item): item is string => typeof item === 'string'))
                }
            }
        } catch {
            // ignore corrupted localStorage entries
        }
    }, [])

    useEffect(() => {
        try {
            localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(savedSearches.slice(0, 20)))
        } catch {
            // best effort only
        }
    }, [savedSearches])

    const searchSuggestions = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return []
        const unique = new Set<string>()
        const matches: string[] = []

        for (const item of remoteSearchSuggestions) {
            const suggestion = (item || '').trim()
            if (!suggestion) continue
            const hay = suggestion.toLowerCase()
            if (hay === q || unique.has(hay)) continue
            if (hay.startsWith(q) || hay.includes(q)) {
                unique.add(hay)
                matches.push(suggestion)
            }
            if (matches.length >= 7) return matches
        }

        for (const item of savedSearches) {
            const suggestion = (item || '').trim()
            if (!suggestion) continue
            const hay = suggestion.toLowerCase()
            if (hay === q || unique.has(hay)) continue
            if (hay.startsWith(q) || hay.includes(q)) {
                unique.add(hay)
                matches.push(suggestion)
            }
            if (matches.length >= 7) break
        }

        return matches
    }, [query, remoteSearchSuggestions, savedSearches])

    useEffect(() => {
        const trimmed = query.trim()
        if (!trimmed || trimmed.length < 2) {
            setRemoteSearchSuggestions([])
            return
        }

        if (suggestTimerRef.current) window.clearTimeout(suggestTimerRef.current)

        suggestTimerRef.current = window.setTimeout(async () => {
            try {
                if (suggestAbortRef.current) suggestAbortRef.current.abort()
                const controller = new AbortController()
                suggestAbortRef.current = controller

                const url = new URL(SUGGEST_URL)
                url.searchParams.set('q', trimmed)
                url.searchParams.set('lang', lang)

                const res = await fetch(url.toString(), { signal: controller.signal })
                if (!res.ok) return
                const data = await res.json()
                const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : []
                setRemoteSearchSuggestions(suggestions.filter((s: unknown): s is string => typeof s === 'string'))
            } catch (err: any) {
                if (err?.name === 'AbortError') return
                setRemoteSearchSuggestions([])
            }
        }, 180)

        return () => {
            if (suggestTimerRef.current) window.clearTimeout(suggestTimerRef.current)
        }
    }, [query, lang])

    const handleSearch = useCallback(async (overrideQuery?: string, isLoadMore = false) => {
        const q = (overrideQuery ?? query).trim()
        if (!q) return

        if (!user) {
            await onRequireLogin()
            return
        }

        if (!isLoadMore) {
            setResults([])
            setNextPageToken(null)
            setTotalSize(null)
            setCorrectedQuery(null)
            setExpandedResultIds({})
        }

        setLoading(true)
        setError(null)

        try {
            const token = await user.getIdToken()
            let url = `https://europe-west6-dxt-data.cloudfunctions.net/search_law?q=${encodeURIComponent(q)}&lang=${lang}`
            if (isLoadMore && nextPageToken) {
                url += `&page_token=${encodeURIComponent(nextPageToken)}`
            }

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

            if (response.status === 402) {
                onShowUpgrade()
                setLoading(false)
                return
            }

            if (!response.ok) {
                const errText = await response.text()
                throw new Error(errText || response.statusText)
            }

            const data = await response.json()
            setResults((prev) => (isLoadMore ? [...prev, ...(data.results || [])] : data.results || []))
            setNextPageToken(data.nextPageToken || null)

            if (!isLoadMore) {
                const total = typeof data.totalSize === 'number' ? data.totalSize : null
                setTotalSize(total)
                setCorrectedQuery(typeof data.correctedQuery === 'string' ? data.correctedQuery : null)
                setSavedSearches((prev) => [q, ...prev.filter((item) => item !== q)].slice(0, 20))
            }
        } catch (err: any) {
            setError(err.message)
            if (!isLoadMore) setResults([])
        } finally {
            setLoading(false)
        }
    }, [lang, nextPageToken, onRequireLogin, onShowUpgrade, query, user])

    const acceptSearchSuggestion = (value: string) => {
        setQuery(value)
        setIsSearchSuggestOpen(false)
        setSearchSuggestIndex(-1)
        handleSearch(value, false)
    }

    const loadMoreRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const sentinel = loadMoreRef.current
        if (!sentinel) return

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0]
                if (!entry?.isIntersecting) return
                if (!nextPageToken || loading) return
                handleSearch(query, true)
            },
            { rootMargin: '200px' }
        )

        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [handleSearch, loading, nextPageToken, query])

    const saveCurrentSearch = () => {
        const q = query.trim()
        if (!q) return
        setSavedSearches((prev) => [q, ...prev.filter((item) => item !== q)].slice(0, 20))
    }

    const removeSavedSearch = (value: string) => {
        setSavedSearches((prev) => prev.filter((item) => item !== value))
    }

    const toggleResultExpanded = (id: string) => {
        setExpandedResultIds((prev) => ({ ...prev, [id]: !prev[id] }))
    }

    return (
        <div className="flex h-full flex-col min-h-0">
            <SearchHeader
                query={query}
                onQueryChange={setQuery}
                onSubmit={() => handleSearch(query)}
                placeholder={t.searchPlaceholder}
                suggestions={searchSuggestions}
                isSuggestOpen={isSearchSuggestOpen}
                suggestIndex={searchSuggestIndex}
                onSuggestVisibilityChange={setIsSearchSuggestOpen}
                onSuggestIndexChange={setSearchSuggestIndex}
                onSuggestionSelect={acceptSearchSuggestion}
                loading={loading}
                hasNextPage={Boolean(nextPageToken)}
                onSaveSearch={saveCurrentSearch}
                t={t}
            />

            <SavedSearchChips
                savedSearches={savedSearches}
                onSelect={(value) => {
                    setQuery(value)
                    handleSearch(value, false)
                }}
                onRemove={removeSavedSearch}
            />

            {error && (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
            )}

            <div className="flex-1 min-h-0">
                <SearchResultsList
                    results={results}
                    loading={loading}
                    totalSize={totalSize}
                    correctedQuery={correctedQuery}
                    nextPageToken={nextPageToken}
                    expandedResultIds={expandedResultIds}
                    onToggleResult={toggleResultExpanded}
                    loadMoreRef={loadMoreRef}
                    t={t}
                />
            </div>
        </div>
    )
}
